import { useEffect, useRef } from "react";
import { X, WarningCircle } from "@phosphor-icons/react";

/**
 * Route Replay Map — Leaflet polyline showing start · waypoints · end for a trip.
 * Leaflet is loaded via CDN in public/index.html (window.L).
 */
export default function RouteMapModal({ trip, onClose }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || !window.L) return;
    const L = window.L;

    const points = [];
    if (trip.start_lat != null) points.push([trip.start_lat, trip.start_lng]);
    for (const wp of (trip.waypoints || [])) points.push([wp.lat, wp.lng]);
    if (trip.end_lat != null) points.push([trip.end_lat, trip.end_lng]);

    const fallback = [20.5937, 78.9629];
    const map = L.map(mapRef.current, { zoomControl: true }).setView(points[0] || fallback, points.length ? 13 : 5);
    mapInstanceRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    if (points.length >= 2) {
      const line = L.polyline(points, { color: "#c8407a", weight: 4, opacity: 0.85 }).addTo(map);
      map.fitBounds(line.getBounds(), { padding: [30, 30] });
    }
    if (trip.start_lat != null) {
      L.circleMarker([trip.start_lat, trip.start_lng], { radius: 7, color: "#059669", fillColor: "#059669", fillOpacity: 0.95, weight: 2 })
        .addTo(map).bindPopup(`<b>Start</b><br/>${trip.start_address || ""}`);
    }
    (trip.waypoints || []).forEach((wp, i) => {
      L.circleMarker([wp.lat, wp.lng], { radius: 4, color: "#00a89c", fillColor: "#00a89c", fillOpacity: 0.7, weight: 1 })
        .addTo(map).bindPopup(`Waypoint ${i + 1}<br/>${new Date(wp.at).toLocaleTimeString()}`);
    });
    if (trip.end_lat != null) {
      L.circleMarker([trip.end_lat, trip.end_lng], { radius: 7, color: "#c8407a", fillColor: "#c8407a", fillOpacity: 0.95, weight: 2 })
        .addTo(map).bindPopup(`<b>End</b><br/>${trip.end_address || ""}`);
    }

    return () => {
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
  }, [trip]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose} data-testid="route-map-modal">
      <div className="bg-white rounded-sm max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-4 border-b border-zinc-200">
          <div>
            <div className="font-display font-bold text-adb-navy">Trip route replay</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              {trip.distance_km ?? 0} km · {trip.mode?.toUpperCase()} · {(trip.waypoints || []).length} waypoint{(trip.waypoints || []).length === 1 ? "" : "s"}{trip.start_at ? ` · started ${new Date(trip.start_at).toLocaleString()}` : ""}
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X size={20} /></button>
        </div>
        <div ref={mapRef} style={{ height: "min(65vh, 560px)", width: "100%" }} data-testid="route-map-canvas" />
        <div className="p-3 border-t border-zinc-200 text-[11px] text-zinc-500 flex flex-wrap gap-3">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-600" /> Start</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-zenith-teal" /> Waypoint (auto every 60 s)</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-zenith-magenta" /> End</span>
          {trip.anomalies?.length > 0 && (
            <span className="ml-auto text-orange-700 inline-flex items-center gap-1">
              <WarningCircle size={12} weight="fill" /> {trip.anomalies.map((a) => a.code.replace(/_/g, " ")).join(" · ")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
